/* jp-layers.js — shared layer manager for the custom mobile drawer, the
   full-screen search overlay, and the PDP size-guide slide-over (Kinzoku §5.4).
   No jQuery / no frameworks. Loaded once; guarded against double-execution.

   Declare a layer with [data-jp-layer="<id>"] on a dialog element. Add
   [data-jp-layer-scrim="false"] for self-backed full-screen layers (the search
   overlay) so the shared scrim is not drawn behind them. Add
   [data-jp-layer-portal="false"] to KEEP a layer in flow (not moved to <body>) —
   for HYBRID layers like the collection filter rail that are a sidebar on desktop
   and a position:fixed drawer on mobile, and whose GET <form> wraps the panel
   (portaling it would reparent the panel out of the form and orphan every input).

   Triggers (event-delegated, so injected markup works too):
     [data-jp-open="<id>"]   opens layer <id>
     [data-jp-close="<id>"]  closes layer <id>
     [data-jp-close]         closes the top layer

   Invariants enforced here:
     · body.no-scroll while any layer is open (scroll-lock)
     · body.jp-layer-open hides the fixed bottom nav under open layers
     · aria-hidden mirrors visibility on each layer
     · focus moves into the layer on open and RETURNS to the opener on close
     · Esc closes the TOP layer (cascade: search > drawer > size-guide > menus)
     · Tab is trapped inside the top layer (offsetParent filter skips hidden nodes)
     · scrim / tap-outside closes the top layer

   Visibility is driven by an .is-open class (NOT [hidden]) so layers can animate
   their transform — see the v2-4 lesson: animate transform, never opacity.

   Public API: window.JapanScissors.layers = { open, close, closeAll, top }. */
(() => {
  if (window.__jpLayersInit) return;
  window.__jpLayersInit = true;

  const stack = [];     // open layer ids; last = top
  const openers = {};   // id -> element to refocus on close
  const openLayers = new Map(); // id -> exact node owning the open state
  const layerSections = new Map(); // node -> original section, retained after portaling
  const focusTimers = new Map();
  const scrollOwners = new Set(); // shared layers + non-layer dialogs (PDP guide/zoom)
  let scrim = null;
  let scrollLockY = 0;  // page scroll captured on first-open, restored when the stack empties (iOS-safe lock)

  const layerEl = (id) => openLayers.get(id) || document.querySelector('[data-jp-layer="' + id + '"]');
  const wantsScrim = (el) => el && el.getAttribute('data-jp-layer-scrim') !== 'false';

  const ensureScrim = () => {
    if (scrim) return scrim;
    scrim = document.getElementById('jp-scrim');
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.id = 'jp-scrim';
      scrim.className = 'jp-scrim';
      document.body.appendChild(scrim);
    }
    scrim.addEventListener('click', closeTop);
    return scrim;
  };

  const focusables = (el) =>
    Array.prototype.slice
      .call(el.querySelectorAll(
        'a[href],area[href],button:not([disabled]),input:not([disabled]),' +
        'select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      ))
      .filter((n) => n.offsetParent !== null);

  const syncScrim = () => {
    const anyScrim = stack.some((id) => wantsScrim(layerEl(id)));
    if (anyScrim) ensureScrim().classList.add('is-open');
    else if (scrim) scrim.classList.remove('is-open');
  };

  const lockScroll = (owner) => {
    const key = String(owner || 'external');
    if (scrollOwners.has(key)) return;
    if (!scrollOwners.size) {
      scrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = -scrollLockY + 'px';
    }
    scrollOwners.add(key);
    document.body.classList.add('no-scroll', 'jp-layer-open', 'jp-scroll-locked');
  };

  const unlockScroll = (owner) => {
    const key = String(owner || 'external');
    if (!scrollOwners.has(key)) return;
    scrollOwners.delete(key);
    if (!scrollOwners.size) {
      document.body.classList.remove('no-scroll', 'jp-layer-open', 'jp-scroll-locked');
      document.body.style.top = '';
      window.scrollTo(0, scrollLockY); // restore the exact pre-open scroll position
    }
  };

  const resetScrollLocks = () => {
    if (!scrollOwners.size) return;
    scrollOwners.clear();
    document.body.classList.remove('no-scroll', 'jp-layer-open', 'jp-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, scrollLockY);
  };

  /* Make everything behind the open layer(s) inert, so AT swipe-navigation
     (VoiceOver/TalkBack) can't escape the focus trap into the page underneath.
     Re-evaluated on every open/close: only the top layer + scrim stay interactive;
     when the stack empties, inert clears. `inert` is a no-op on old browsers.

     We track EXACTLY which elements we set inert (inertedByLayers) and only ever
     clear those — so we never wipe inert that another isolator set (the PDP
     size-guide + zoom lightbox in jp-product.js keep their own bookkeeping). An
     element already inert when we arrive is left to its owner (skipped, and not
     recorded as ours), so the two systems coexist without fighting. */
  const inertedByLayers = new Set();
  const skipIsolationNode = (el) => {
    if (!el || el.id === 'jp-scrim') return true;
    return ['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(el.tagName);
  };
  const refreshInert = () => {
    inertedByLayers.forEach((el) => { el.inert = false; });
    inertedByLayers.clear();
    const active = layerEl(stack[stack.length - 1]);
    if (!active) return;
    /* Isolate at EVERY ancestor boundary. This is essential for non-portaled
       layers such as collection filters: keeping only the top body child would
       otherwise leave the rest of <main> interactive. It also makes an
       underlying open dialog inert when another layer is stacked above it. */
    let node = active;
    while (node && node !== document.body && node.parentElement) {
      Array.prototype.forEach.call(node.parentElement.children, (sibling) => {
        if (sibling === node || skipIsolationNode(sibling) || sibling.inert) return;
        sibling.inert = true;
        inertedByLayers.add(sibling);
      });
      node = node.parentElement;
    }
  };

  const syncLayerVisibility = () => {
    const activeId = stack[stack.length - 1];
    stack.forEach((layerId) => layerEl(layerId)?.setAttribute('aria-hidden', layerId === activeId ? 'false' : 'true'));
  };

  function open(id, opener) {
    const el = layerEl(id);
    if (!el || stack.indexOf(id) !== -1) return;
    openers[id] = opener || document.activeElement;
    if (opener && opener.hasAttribute && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'true');
    stack.push(id);
    openLayers.set(id, el);
    el.classList.add('is-open');
    syncLayerVisibility();
    lockScroll('layer:' + id);
    syncScrim();
    refreshInert();
    // Focus the autofocus target (e.g. the search input) SYNCHRONOUSLY — iOS Safari only raises the software
    // keyboard for a focus that happens inside the opening tap's call stack; a setTimeout drops out of it and
    // the keyboard silently doesn't appear. Non-input layers keep the deferred first-focusable (no back-button flash).
    const _sel = el.getAttribute('data-jp-autofocus');
    const _target = _sel && el.querySelector(_sel);
    if (_target) {
      try { _target.focus({ preventScroll: true }); } catch (e) { _target.focus(); }
    } else {
      focusTimers.set(id, window.setTimeout(() => {
        focusTimers.delete(id);
        if (openLayers.get(id) !== el || !el.isConnected || top() !== id || !el.classList.contains('is-open')) return;
        (focusables(el)[0] || el).focus();
      }, 60));
    }
    document.dispatchEvent(new CustomEvent('jp:layer:open', { detail: { id } }));
  }

  function close(id, options) {
    const i = stack.indexOf(id);
    if (i === -1) return;
    const wasTop = i === stack.length - 1;
    const el = layerEl(id);
    if (focusTimers.has(id)) window.clearTimeout(focusTimers.get(id));
    focusTimers.delete(id);
    stack.splice(i, 1);
    openLayers.delete(id);
    if (el) {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
    }
    syncLayerVisibility();
    syncScrim();
    unlockScroll('layer:' + id);
    const opener = openers[id];
    delete openers[id];
    if (opener && opener.hasAttribute && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'false');
    refreshInert(); // release the newly exposed underlying layer before returning focus into it
    if (wasTop && options?.restoreFocus !== false && opener && opener.isConnected !== false && typeof opener.focus === 'function') {
      // Scroll is restored above; returning focus to a fixed header/nav opener
      // must not immediately scroll the document back to that control.
      try { opener.focus({ preventScroll: true }); } catch (e) { opener.focus(); }
    }
    document.dispatchEvent(new CustomEvent('jp:layer:close', { detail: { id } }));
  }

  const top = () => stack[stack.length - 1];
  function closeTop() { const t = top(); if (t) close(t); }
  function closeAll() { stack.slice().reverse().forEach(close); }

  /* A responsive surface can disappear while a layer opened from inside it is
     still active (drawer -> search, then rotate to desktop). Re-target the
     pending focus return instead of letting the top layer close back into a
     hidden control. Keep aria-expanded truthful on both triggers. */
  const setOpener = (id, opener) => {
    if (stack.indexOf(id) === -1 || !opener) return false;
    const previous = openers[id];
    if (previous && previous.hasAttribute && previous.hasAttribute('aria-expanded')) {
      previous.setAttribute('aria-expanded', 'false');
    }
    openers[id] = opener;
    if (opener.hasAttribute && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'true');
    return true;
  };

  window.JapanScissors = window.JapanScissors || {};
  window.JapanScissors.layers = { open, close, closeAll, top, setOpener, lockScroll, unlockScroll, resetScrollLocks };

  /* delegated open/close triggers */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-jp-open]');
    if (opener) {
      const id = opener.getAttribute('data-jp-open');
      if (layerEl(id)) {        // only hijack when the layer exists; otherwise let
        e.preventDefault();     // the element behave normally (e.g. an <a href> fallback)
        open(id, opener);
        return;
      }
    }
    const closer = e.target.closest('[data-jp-close]');
    if (closer) {
      e.preventDefault();
      const id = closer.getAttribute('data-jp-close');
      if (id) close(id); else closeTop();
    }
  });

  /* Esc closes the top layer; Tab is trapped within it */
  document.addEventListener('keydown', (e) => {
    if (!stack.length) return;
    if (e.key === 'Escape') { e.preventDefault(); closeTop(); return; }
    if (e.key !== 'Tab') return;
    const el = layerEl(top());
    if (!el) return;
    const f = focusables(el);
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* Retain the owning section before portaling. Replacement closes the exact
     previous node first, releasing its stack/scroll/inert/focus ownership. */
  const portalLayers = () => {
    layerSections.forEach((section, el) => {
      if (section.isConnected !== false) return;
      const id = el.getAttribute('data-jp-layer');
      if (openLayers.get(id) === el) close(id);
      if (el.parentElement === document.body) el.remove();
      layerSections.delete(el);
    });
    document.querySelectorAll('[data-jp-layer]').forEach((el) => {
      if (el.isConnected === false) return;
      const id = el.getAttribute('data-jp-layer');
      const section = el.closest?.('[id^="shopify-section-"]');
      if (section) layerSections.set(el, section);
      const previous = openLayers.get(id);
      if (previous && previous !== el) close(id);
      if (el.getAttribute('data-jp-layer-portal') === 'false') return;
      if (el.parentElement === document.body) return;
      document.querySelectorAll('body > [data-jp-layer="' + id + '"]').forEach((old) => {
        if (old === el) return;
        layerSections.delete(old);
        old.remove();
      });
      document.body.appendChild(el);
    });
    // A replacement may remove a hybrid layer without introducing another one.
    stack.slice().reverse().forEach((id) => {
      if (openLayers.get(id)?.isConnected === false) close(id);
    });
    layerSections.forEach((section, el) => {
      if (el.isConnected === false) layerSections.delete(el);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', portalLayers);
  else portalLayers();
  document.addEventListener('shopify:section:load', portalLayers);
  document.addEventListener('shopify:section:unload', (event) => {
    const owned = Array.from(layerSections).filter(([el, section]) =>
      section === event.target || event.target.contains?.(section) || event.target.contains?.(el));
    const nodes = new Set(owned.map(([el]) => el));
    stack.slice().reverse().forEach((id) => {
      if (!nodes.has(openLayers.get(id))) return;
      const opener = openers[id];
      const removingOpener = owned.some(([el, section]) => el.contains?.(opener) || section.contains?.(opener));
      close(id, { restoreFocus: !removingOpener });
    });
    owned.forEach(([el]) => {
      layerSections.delete(el);
      if (el.parentElement === document.body) el.remove();
    });
  });

  /* close everything on navigation away (bfcache safety), including dialogs
     which borrow only the shared scroll lock rather than the layer stack. */
  window.addEventListener('pagehide', () => {
    closeAll();
    resetScrollLocks();
    document.dispatchEvent(new CustomEvent('jp:layers:reset'));
  });
})();
